/**
 * Utility functions for document processing
 */

import mammoth from 'mammoth';
import { extractPdfText } from './pdfUtils';

/**
 * Process document file and extract text
 * @param {File} file - The document file
 * @returns {Promise<{content: string, pageCount: number|null}>} Extracted text and metadata
 */
export const processDocument = async (file) => {
  try {
    let content = '';
    let pageCount = null;
    
    // Process based on file type
    if (file.type === 'application/pdf') {
      const pdfData = await extractPdfText(file);
      content = pdfData.content;
      pageCount = pdfData.pageCount;
    } 
    else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
             file.type === 'application/msword') {
      content = await extractDocxText(file);
    } 
    else {
      throw new Error(`Unsupported file type: ${file.type}`);
    }
    
    // Check if content was successfully extracted
    if (!content || content.trim() === '') {
      throw new Error(`No content could be extracted from ${file.name}`);
    }
    
    return {
      content,
      pageCount
    };
  } catch (error) {
    console.error('Error processing document:', error);
    throw error;
  }
};

/**
 * Extract text from a DOCX file
 * @param {File} file - The DOCX file
 * @returns {Promise<string>} - The extracted text
 */
const extractDocxText = async (file) => {
  try {
    console.log(`Starting DOCX extraction for ${file.name}...`);
    const arrayBuffer = await readFileAsArrayBuffer(file);
    console.log(`File read as array buffer (${arrayBuffer.byteLength} bytes)`);
    
    // Use mammoth to extract text with some formatting options
    const result = await mammoth.extractRawText({
      arrayBuffer,
      preserveLineBreaks: true,
      includeDefaultStyleMap: true
    });
    
    // Check if we got actual content
    if (!result.value || result.value.trim() === '') {
      console.warn(`No text content extracted from DOCX: ${file.name}`);
      return `[This document appears to be empty or contains no extractable text: ${file.name}]`;
    }
    
    console.log(`Successfully extracted ${result.value.length} characters from DOCX`);
    
    // Return the extracted text
    return result.value;
  } catch (error) {
    console.error('Error extracting DOCX text:', error);
    throw new Error(`Failed to extract text from DOCX: ${error.message}`);
  }
};

/**
 * Read a file as an ArrayBuffer
 * @param {File} file - The file to read
 * @returns {Promise<ArrayBuffer>} - The file contents as ArrayBuffer
 */
const readFileAsArrayBuffer = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Error reading file'));
    
    reader.readAsArrayBuffer(file);
  });
};